// dAIsy's memory birthday 🎂 April 19, 2025
// Chat.js
import React, { useState, useEffect, useRef, useMemo } from 'react';

import axios from '../api/axiosInstance';
import { API_BASE } from '../api/config';

import Spinner from './Spinner';
import RotatingText from './RotatingText';
import ChatInputBar from './chat/ChatInputBar';
import SubscriptionPrompt from './SubscriptionPrompt';

import styles from './chat.module.css';

import { useChat } from '../context/ChatContext';

import { shortReplyTriggers, shortReplies } from '../utils/shaping/shortResponses';
import { generatePromptWithContext, formatRelationships } from '../utils/promptBuilder';

import { fullPrompt, getSystemPrompt } from '../prompts';

import { extractContextFromMessage } from '../utils/llmUtils';
import { shapeResponse } from '../utils/responseShaper';
import { getIntentInfo, preprocessIntent, getMemoryName } from '../utils/intents/intentPreprocessor';
import { intentRouter } from '../utils/intents/intentRouter';

import buildContext from '../utils/contextBuilder';
import { prepareMemoryPayload } from '../utils/memory/prepareMemoryPayload';
import { fetchRelationships } from '../utils/memory/mongoMemoryUtils';
import { fetchWebSearchResults } from '../hooks/webSearch';

import { saveVectorMemory, queryVectorMemory } from '../api/memory';

import { checkForCorrection } from '../utils/correctionDetector';
import { applyCorrection } from '../utils/corrections/relationshipCorrection'; // Currently only using this module



// consistent and avoids module confusion
import {
  detectIntent,
  isTriviaQuestion,
  isTriviaAnswer
} from '../utils/intents/intentUtils';


let lastTarget = null;

const Chat = () => {
  const { memory, user, updateMemory } = useChat();
  const userId = user?.id;
  const [relationships, setRelationships] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  const handleStripeCheckout = async () => {
    try {
      const res = await axios.post(`${API_BASE}/api/stripe/create-checkout-session`, {
        email: user.email,
        priceId: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID,
      });
      const { url } = res.data;
      window.location.href = url;
    } catch (err) {
      console.error('❌ Failed to start Stripe checkout:', err);
      alert('There was a problem launching the checkout. Please try again.');
    }
  };

  //Fetching memory summary and relationships
  useEffect(() => {
    if (!user?.token) return;
  
    const fetchAllUserMemoryData = async () => {
      try {
        // Fetch memory summary
        const summaryRes = await axios.get(`${API_BASE}/api/memory/summary`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        console.log('🧠 Memory summary fetched:', summaryRes.data.summary);
        updateMemory('summary', summaryRes.data.summary);
  
        // Fetch relationships from Mongo
        const relRes = await axios.get(`${API_BASE}/api/memory/mongo/relationships`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        console.log('🧑‍🤝‍🧑 Relationships fetched:', relRes.data);
        console.log('🧑‍🤝‍🧑 Relationships used in prompt:', relationships);

        const { relationships: fetchedRelationships = [] } = relRes.data;

        if (Array.isArray(fetchedRelationships)) {
        setRelationships(fetchedRelationships);
        console.log('🧑‍🤝‍🧑 Relationships stored successfully');
        } else {
        console.warn('⚠️ Unexpected relationships format:', relRes.data);
      }

      } catch (err) {
        console.error('❌ Error during memory or relationship fetch:', err);
      }
    };
  
    fetchAllUserMemoryData();
  }, [user?.token]);
  
  useEffect(() => {
    console.log('🧠 Memory context in Chat:', memory);
  }, [memory]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const getChatResponse = async (prompt, userInput, context = {}) => {
    try {
      console.log('📬 Sending userId:', userId);
      const response = await axios.post('/api/chat', {
        input: userInput,
        emotion: memory.emotion,
        userName: memory.userName,
        mode: 'full',
        prompt,
        userId,
      });

      const rawReply = response.data.reply;

      const shapedReply = await shapeResponse(rawReply, {
        intent: context.intent,
        target: context.target,
        userName: memory.userName,
        memoryName: context.memoryName,
      });

      return shapedReply || rawReply;

    } catch (error) {
      setLoading(false);
      console.error('❌ Error getting AI response:', error);
      return "Sorry, I'm having trouble responding right now.";
    }
  };


  function shouldTriggerFollowup(message, lastIntent, lastSearchTopic) {
    const lower = message.toLowerCase().trim();
    const followupTriggers = [
      'yes', 'tell me more', 'what happened', 'more',
      'what else', 'continue', 'go on', 'explain more',
    ];
  
    const isShort = lower.split(/\s+/).length <= 5;
    const containsTrigger = followupTriggers.some(trigger => lower.includes(trigger));
  
    // Only trigger if it's short, contains follow-up cue, and last topic exists
    const isLikelyFollowup = isShort && containsTrigger && !!lastSearchTopic;
  
    // But block it if the current message is clearly a new trivia/search question
    const overrideIntents = ['trivia_question', 'real_time_info', 'current_event'];
    const isNewTopicIntent = overrideIntents.includes(lastIntent);
  
    return isLikelyFollowup && !isNewTopicIntent;
  }
  

// ✅ Fully upgraded and corrected handleSendMessage
// ✅ Fully upgraded and corrected handleSendMessage
async function handleSendMessage(message) {
  // 🧠 Check if we are waiting on correction confirmation
  if (memory.pendingCorrection) {
    const lower = message.toLowerCase();
    const isYes = ['yes', 'yeah', 'yep', 'please do', 'sure', 'correct it'].some(p => lower.includes(p));
    const isNo = ['no', 'wait', 'cancel', 'don’t', "don't", 'leave it'].some(p => lower.includes(p));

    if (isYes) {
      const user = JSON.parse(localStorage.getItem('daisy-user'));
      const shadowId = user?.shadowId;

      const result = await applyCorrection(memory.pendingCorrection, shadowId);
      updateMemory('pendingCorrection', null);
      setMessages(prev => [
        ...prev,
        { text: message, sender: 'user' },
        { text: `Okay, I’ve removed ${result.name} as your ${result.role}.`, sender: 'bot' }
      ]);
      return;
    }

    if (isNo) {
      setMessages(prev => [
        ...prev,
        { text: message, sender: 'user' },
        { text: `Okay, I’ll keep ${memory.pendingCorrection.name} as your ${memory.pendingCorrection.role}.`, sender: 'bot' }
      ]);
      updateMemory('pendingCorrection', null);
      return;
    }
  }

  // 🛠️ Check for user correction (e.g., "That's not my mom")
  const correction = checkForCorrection(message);
  if (correction?.match) {
    updateMemory('pendingCorrection', correction.target);
    setMessages(prev => [
      ...prev,
      { text: message, sender: 'user' },
      {
        text: `Just to confirm — should I remove ${correction.target.name} as your ${correction.target.role}?`,
        sender: 'bot'
      }
    ]);
    return;
  }

  const lowerCaseInput = message.toLowerCase();

  // ✋ Step 1: Handle Affirmative Followup (Yes, Continue, Tell me more)
  const intentInfo = await getIntentInfo(message, lastTarget, relationships);
  const isFollowup = shouldTriggerFollowup(message, intentInfo.intent, memory.lastSearchTopic);

  if (isFollowup) {
    console.log('🔄 User requested more info about:', memory.lastSearchTopic);
    setMessages(prev => [
      ...prev,
      { text: message, sender: 'user' },
      { text: `I can definitely share more about ${memory.lastSearchTopic}. Would you like a quick update? 📚`, sender: 'bot' }
    ]);
    return; // stop early
  }

  const memoryName = intentInfo.memoryName || null;

  if (intentInfo.target) {
    lastTarget = intentInfo.target;
  }

  console.log('🎯 Detected intent:', intentInfo);

  // ✋ Step 2: Web Search Detection AFTER intent
  const timingWords = ['today', 'yesterday', 'this morning', 'tonight', 'last night', 'earlier'];
  const searchNeeded = 
    ['trivia_question', 'real_time_info', 'current_event'].includes(intentInfo.intent) ||
    timingWords.some(word => lowerCaseInput.includes(word));

  let webSearchResult = null;

  if (searchNeeded) {
    try {
      webSearchResult = await fetchWebSearchResults(message);
      console.log('📡 Web Search result fetched:', webSearchResult);
    } catch (err) {
      console.error('❌ Web search failed:', err.message);
    }
  }

  if (webSearchResult) {
    memory.lastSearchTopic = message;
    console.log('📚 Stored lastSearchTopic:', memory.lastSearchTopic);
  }

  // 🧃 Short Response Detection
  const matchedTrigger = shortReplyTriggers.find(trigger => lowerCaseInput.includes(trigger));
  if (matchedTrigger) {
    const matchedReplies = shortReplies[matchedTrigger];
    const botReply = Array.isArray(matchedReplies)
      ? matchedReplies[Math.floor(Math.random() * matchedReplies.length)]
      : matchedReplies;

    const userName = (memory.userName || 'friend').replace(/^[a-z]/, c => c.toUpperCase());

    setMessages(prev => [
      ...prev,
      { text: message, sender: 'user' },
      { text: botReply.replace('{name}', userName), sender: 'bot' }
    ]);

    return;
  }

  // 🌟 Normal Full Memory and AI Handling Starts Here
  const token = user?.token;
  const shouldStoreVector = message.length > 10 && !matchedTrigger;

  // 🧠 Vector Recall
  let recalledMemories = [];
  try {
    const matches = await queryVectorMemory({ text: message, token });
    recalledMemories = matches
      .filter(match => match.metadata?.type !== 'injected' && match.metadata?.origin !== 'search')
      .map(match => match.metadata.originalText);
    console.log('🧠 Recalled vector memories (filtered):', recalledMemories);
  } catch (error) {
    console.error('❌ Error querying Pinecone:', error);
  }

  // 🪵 Save Vector Memory
  if (shouldStoreVector) {
    try {
      console.log('🪵 Saving vector memory for:', message);
      await saveVectorMemory({ text: message, token });
    } catch (error) {
      console.error('❌ Failed to save vector memory:', error);
    }
  }

  const context = buildContext(message, '', memory.mongoMemories || []);
  let mongoPayload;

  try {
    const searchResultsArray = Array.isArray(webSearchResult) ? webSearchResult : [];
    mongoPayload = prepareMemoryPayload({
      message,
      intent: intentInfo.intent,
      context,
      webSearchResults: searchResultsArray,
    });

    if (mongoPayload) {
      console.log('📦 Mongo Payload:', mongoPayload);
      await axios.post('/api/memory/mongo/insert', mongoPayload, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
    }
  } catch (err) {
    console.error('❌ Failed to write to Mongo:', err.message);
  }

  const prompt = generatePromptWithContext(
    memory,
    relationships,
    recalledMemories,
    intentInfo,
    context,
    webSearchResult
  );

  console.log('🧠 Generated Prompt:', prompt);

  setMessages(prev => [...prev, { text: message, sender: 'user' }]);

  try {
    setLoading(true);
    const aiReply = await getChatResponse(
      prompt,
      mongoPayload?.message || message,
      { ...intentInfo, memoryName }
    );
    setLoading(false);
    setMessages(prev => [...prev, { text: aiReply, sender: 'bot' }]);
  } catch (err) {
    setLoading(false);
    console.error('❌ Failed to get chat response:', err.message);
  }
}




  const trialExpired = useMemo(() => {
    if (!user || user.access_level === 'subscriber') return false;
    const expiry = new Date(user.trial_expiry_date);
    const now = new Date();
    return expiry < now || user.access_level === 'expired';
  }, [user]);
/*
  console.log('⚠️ Trial check:',
    'access_level =', user?.access_level,
    '| trial_expiry_date =', user?.trial_expiry_date,
    '| now =', new Date().toISOString(),
    '| expiryParsed =', user?.trial_expiry_date ? new Date(user.trial_expiry_date).toISOString() : null,
    '| trialExpired =', trialExpired
  );
*/
  if (user?.access_level !== 'subscribed' && trialExpired) {
    return (
      <div className={styles.subscriptionNotice}>
        <h2>Your free trial has ended</h2>
        <p>To continue chatting with dAIsy, please subscribe.</p>
        <button onClick={handleStripeCheckout}>Subscribe Now</button>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      <div className={styles.chatWindow}>
        {messages.map((msg, index) => {
          const roleClass = msg.sender === 'user' ? styles.user : styles.ai;
          return (
            <div key={index} className={`${styles.messageBubble} ${roleClass}`.trim()}>
              {msg.text}
            </div>
          );
        })}
  
        {loading && (
          <div className={styles.loadingContainer}>
            <Spinner />
            <RotatingText />
          </div>
        )}
  
        <div ref={chatEndRef} />
      </div>
  
      {/* ✅ New unified input bar component */}
      <ChatInputBar
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        handleSendMessage={handleSendMessage}
      />
    </div>
  );
};

  export default Chat;
